import useGenres from '@/hooks/useGenres';
import { getCroppedImageUrl } from '@/services/image-service';
import useGameStore from '@/store';
import { useT } from '@/i18n/useLanguage';
import { Box, Flex, Text, Skeleton } from '@chakra-ui/react';
import { Image } from '@chakra-ui/react/image';
import { useRef } from 'react';
import { LuChevronLeft, LuChevronRight, LuLayoutGrid } from 'react-icons/lu';

const GenreStrip = () => {
	const selectedGenreId = useGameStore((s) => s.gameQuery.genreId);
	const setSelectedGenreId = useGameStore((s) => s.setGenreId);
	const t = useT();
	const { data: genres, isLoading, error } = useGenres();
	const scrollerRef = useRef<HTMLDivElement>(null);

	if (error) return null;

	const scrollBy = (dir: number) => {
		const el = scrollerRef.current;
		if (!el) return;
		el.scrollBy({ left: dir * 280, behavior: 'smooth' });
	};

	return (
		<Box position="relative" mb={2}>
			<Flex align="center" justify="space-between" mb={3}>
				<Text
					fontSize="xs"
					fontWeight="700"
					textTransform="uppercase"
					letterSpacing="widest"
					color="fg.muted"
				>
					{t('genres')}
				</Text>
				<Flex gap={1}>
					<Box
						as="button"
						onClick={() => scrollBy(-1)}
						aria-label="Scroll left"
						w={8}
						h={8}
						display="flex"
						alignItems="center"
						justifyContent="center"
						rounded="full"
						bg="bg.muted"
						borderWidth="1px"
						borderColor="border"
						_hover={{ bg: 'bg.elevated', borderColor: 'brand.400' }}
						transition="all 0.15s"
					>
						<LuChevronLeft size={16} />
					</Box>
					<Box
						as="button"
						onClick={() => scrollBy(1)}
						aria-label="Scroll right"
						w={8}
						h={8}
						display="flex"
						alignItems="center"
						justifyContent="center"
						rounded="full"
						bg="bg.muted"
						borderWidth="1px"
						borderColor="border"
						_hover={{ bg: 'bg.elevated', borderColor: 'brand.400' }}
						transition="all 0.15s"
					>
						<LuChevronRight size={16} />
					</Box>
				</Flex>
			</Flex>

			<Box
				ref={scrollerRef}
				overflowX="auto"
				overflowY="hidden"
				css={{
					scrollbarWidth: 'none',
					'&::-webkit-scrollbar': { display: 'none' },
					scrollSnapType: 'x mandatory',
				}}
				pb={1}
			>
				<Flex gap={3} minW="min-content">
					{/* All genres card */}
					<Box
						as="button"
						onClick={() => setSelectedGenreId(undefined)}
						flexShrink={0}
						w={{ base: '110px', md: '130px' }}
						h={{ base: '72px', md: '88px' }}
						rounded="2xl"
						position="relative"
						overflow="hidden"
						borderWidth="2px"
						borderColor={!selectedGenreId ? 'brand.400' : 'border'}
						bg={!selectedGenreId ? 'brand.500/20' : 'bg.muted'}
						boxShadow={!selectedGenreId ? '0 0 0 1px var(--chakra-colors-brand-400)' : undefined}
						_hover={{ transform: 'translateY(-2px)', borderColor: 'brand.400' }}
						transition="all 0.2s ease"
						css={{ scrollSnapAlign: 'start' }}
					>
						<Flex
							h="100%"
							direction="column"
							align="center"
							justify="center"
							gap={1.5}
							px={2}
						>
							<Box
								color={!selectedGenreId ? 'brand.400' : 'fg.muted'}
								display="flex"
							>
								<LuLayoutGrid size={22} />
							</Box>
							<Text
								fontSize="xs"
								fontWeight="700"
								color={!selectedGenreId ? 'brand.400' : 'fg'}
								textAlign="center"
								lineClamp={1}
							>
								{t('allGenres')}
							</Text>
						</Flex>
					</Box>

					{isLoading &&
						[1, 2, 3, 4, 5, 6].map((i) => (
							<Skeleton
								key={i}
								flexShrink={0}
								w={{ base: '110px', md: '130px' }}
								h={{ base: '72px', md: '88px' }}
								rounded="2xl"
							/>
						))}

					{!isLoading &&
						genres?.map((genre) => {
							const active = selectedGenreId === genre.id;
							return (
								<Box
									key={genre.id}
									as="button"
									onClick={() => setSelectedGenreId(genre.id)}
									flexShrink={0}
									w={{ base: '110px', md: '130px' }}
									h={{ base: '72px', md: '88px' }}
									rounded="2xl"
									position="relative"
									overflow="hidden"
									borderWidth="2px"
									borderColor={active ? 'brand.400' : 'transparent'}
									boxShadow={active ? '0 0 20px rgba(139,92,246,0.35)' : undefined}
									_hover={{ transform: 'translateY(-3px)' }}
									transition="all 0.2s ease"
									css={{ scrollSnapAlign: 'start' }}
								>
									<Image
										src={getCroppedImageUrl(genre.image_background)}
										alt=""
										position="absolute"
										inset={0}
										w="100%"
										h="100%"
										objectFit="cover"
										transform={active ? 'scale(1.08)' : 'scale(1)'}
										transition="transform 0.3s ease"
									/>
									{/* dark gradient for readable text */}
									<Box
										position="absolute"
										inset={0}
										bgGradient="to-t"
										gradientFrom="blackAlpha.900"
										gradientTo="blackAlpha.400"
									/>
									{active && (
										<Box
											position="absolute"
											inset={0}
											bg="brand.500/25"
										/>
									)}
									<Flex
										position="relative"
										h="100%"
										align="flex-end"
										justify="center"
										p={2.5}
									>
										<Text
											fontSize="xs"
											fontWeight="800"
											color="white"
											textAlign="center"
											lineClamp={2}
											textShadow="0 1px 8px rgba(0,0,0,0.8)"
											lineHeight="1.2"
										>
											{genre.name}
										</Text>
									</Flex>
								</Box>
							);
						})}
				</Flex>
			</Box>
		</Box>
	);
};

export default GenreStrip;
